/* oxlint-disable react/destructuring-assignment */
import { TextAreaInput, TextInput } from "@jugl-web/ui-kit";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { formatSecondsToDuration } from "@jugl-web/utils/date-time/formatSecondsToDuration";
import { format } from "date-fns";
import debounce from "lodash/debounce";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { MAX_NOTES_LENGTH } from "../../consts";
import { useActiveTimeTrackingNotesCache } from "../../hooks/useActiveTimeTrackingNotesCache";
import { formatTimeLogDateLabel } from "../../utils";
import { AddTimeLogButton } from "../AddTimeLogButton";
import { CurrentTimeInput } from "../CurrentTimeInput";
import { CurrentTimerText } from "../CurrentTimerText";
import { PlayTimerButton } from "../PlayTimerButton";
import { StopTimerButton } from "../StopTimerButton";

export type TimeTrackingStatusPanelTypeSpecificProps =
  | { type: "idle"; totalDuration: number }
  | { type: "active"; timeLogId: string; startDate: Date };

export type TimeTrackingStatusPanelProps =
  TimeTrackingStatusPanelTypeSpecificProps & {
    onStopTimer?: () => void | Promise<unknown>;
    onStartTimer?: () => void | Promise<unknown>;
    onAddTimeLog?: () => void;
  };

export const TimeTrackingStatusPanel: FC<TimeTrackingStatusPanelProps> = ({
  onStartTimer,
  onStopTimer,
  onAddTimeLog,
  ...props
}) => {
  const { getCachedNotes, cacheNotes } = useActiveTimeTrackingNotesCache();

  const [notes, setNotes] = useState(() =>
    props.type === "active" ? getCachedNotes(props.timeLogId) : ""
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isMobile } = useAppVariant();

  const { t } = useTranslations();

  const debouncedCacheNotes = useMemo(
    () => debounce(cacheNotes, 200),
    [cacheNotes]
  );

  const handleNotesChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value);

    if (props.type === "active") {
      debouncedCacheNotes(props.timeLogId, event.target.value);
    }
  };

  useEffect(() => () => debouncedCacheNotes.flush(), [debouncedCacheNotes]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="font-secondary text-lg leading-[140%] font-semibold tracking-[0.18px] text-dark">
            {props.type === "idle" ? (
              formatSecondsToDuration(props.totalDuration)
            ) : (
              <CurrentTimerText startDate={props.startDate} />
            )}
          </span>
          <span className="font-secondary text-xs text-dark-600">
            {props.type === "idle" ? (
              t({
                id: "tasks-page.task-total",
                defaultMessage: "Task total",
              })
            ) : (
              <div className="flex items-center gap-1.5 leading-base">
                {t({
                  id: "tasks-page.current-timer",
                  defaultMessage: "Current timer",
                })}
                <span className="text-xl font-bold text-dark-600">
                  &middot;
                </span>
                {formatTimeLogDateLabel({
                  startDate: props.startDate,
                  endDate: null,
                })}
              </div>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {props.type === "idle" ? (
            <>
              <PlayTimerButton
                isDisabled={isSubmitting}
                onClick={async () => {
                  setIsSubmitting(true);
                  await onStartTimer?.();
                  setIsSubmitting(false);
                }}
              />
              <AddTimeLogButton onClick={onAddTimeLog} />
            </>
          ) : (
            <StopTimerButton
              isDisabled={isSubmitting}
              onClick={async () => {
                setIsSubmitting(true);
                await onStopTimer?.();
                setNotes("");
                setIsSubmitting(false);
              }}
            />
          )}
        </div>
      </div>
      {props.type === "active" && (
        <div className="flex flex-col gap-6">
          <div className={cx("flex flex-col gap-4", !isMobile && "flex-row")}>
            <TextInput
              label={t({ id: "common.from", defaultMessage: "From" })}
              value={format(props.startDate, "hh:mm a")}
              isDisabled
              readOnly
              classNames={{
                wrapperClassName: "space-y-0.5 w-full",
                labelClassName: "text-center text-xs text-dark-600",
                inputClassName: "text-sm h-10 px-3 text-dark",
              }}
            />
            <CurrentTimeInput
              size={isMobile ? "small" : "default"}
              label={t({ id: "common.to", defaultMessage: "To" })}
              classNames={{
                wrapperClassName: "space-y-0.5 w-full",
                labelClassName: "text-center text-xs text-dark-600",
                inputClassName: "text-sm h-10 px-3 text-dark",
              }}
            />
          </div>
          <TextAreaInput
            placeholder={t({
              id: "tasks-page.add-notes",
              defaultMessage: "Add notes",
            })}
            maxLength={MAX_NOTES_LENGTH}
            lengthIndicator={{ max: MAX_NOTES_LENGTH }}
            value={notes}
            onChange={handleNotesChange}
            classNames={{
              inputClassName: cx(
                "text-sm text-dark max-h-[300px]",
                !isMobile && "min-h-[100px]"
              ),
            }}
          />
        </div>
      )}
    </div>
  );
};
